<template>
    <form
        ref="form"
        @submit.prevent="submit"
        :action="formRoute"
        method="POST"
        class="relative h-full"
    >
        <page-header
            :submit-button-text="data ? 'Update' : 'Create Category'"
            :show-submit-button="true"
            :submit-button-disabled="submitButtonDisabled || !editable"
            submit-button-type="submit"

            :can-delete="canDelete"
            :delete-route="deleteRoute"
            :delete-value="deleteValue"

            :can-create="canCreate"
            :create-route="createRoute"
            :create-value="createValue"

            :index-route="indexRoute"
            :index-value="indexValue"
            :title="title"
            :subtitle="subtitle"

            :lockable="lockable"
            :has-lock="hasLock"
            :document-lock="documentLock"
            @force-takeover="attemptTakeover"
            @takeover="takeOver"

            :show-preview-button="shouldShowPreviewButton()"
            @click-preview="submitPreviewSite(form.language.iso, form.uri)"

            :errors="errors"
            :is-slide-over="isSlideOver"

            :seo-data="data ? data.latest_seo : null"
            model="App\Models\Category"

            :version-id="versionId"
            :init-version-id="initVersionId"
            :last-updated-by="lastUpdatedBy"
        />

        <input-rich-text
            name="editor_note"
            label="Editor's note"
            v-model="form.editor_note"
            :language="form.language"
            :error="errors.editor_note ? errors.editor_note[0] : null"
            :maxlength="60000"
        />
        <p class="my-1 text-xs text-gray-600 dark:text-gray-300 mb-3">The note is for internal use only and won’t be displayed on the site</p>

        <div
            class="border bg-white dark:bg-gray-700 rounded-lg w-full p-4 shadow-sm max-w-4xl mb-3"
            v-if="typeId === 2"
        >
            <p class="text-gray-700 dark:text-gray-200 mb-4">* Software data taken from:</p>
            <reference
                create-text="Create new software and link software to this page"
                link-text="Link an existing software"
                v-model="form.software"
                model-data="Software"
                :should-open-new-window="true"
                :language="form.language"
                :error="errors.software_id ? errors.software_id[0] : null"
                :creatable="false"
                :query="{
                    variant_of: getBaseDocumentDataId('software_id'),
                }"
                :order="{
                    'name': 'asc'
                }"
            />
        </div>

        <div
            class="border bg-white dark:bg-gray-700 rounded-lg w-full p-4 shadow-sm max-w-4xl mb-3"
            v-if="typeId === 3"
        >
            <p class="text-gray-700 dark:text-gray-200 mb-4">* Payment Method data taken from:</p>
            <reference
                create-text="Create new payment method and link payment method to this page"
                link-text="Link an existing payment method"
                v-model="form.payment"
                :language="form.language"
                :should-open-new-window="true"
                model-data="Payment Method"
                :error="errors.payment_id ? errors.payment_id[0] : null"
                :creatable="false"
                :query="{
                    variant_of: getBaseDocumentDataId('payment_id'),
                }"
                :order="{
                    'name': 'asc'
                }"
            />
        </div>

        <div
            class="border bg-white dark:bg-gray-700 rounded-lg w-full p-4 shadow-sm max-w-4xl mb-3"
            v-if="typeId === 4"
        >
            <p class="text-gray-700 dark:text-gray-200 mb-4">* License data taken from:</p>
            <reference
                create-text="Create new license and link license to this page"
                link-text="Link an existing license"
                v-model="form.license"
                :language="form.language"
                model-data="License"
                :should-open-new-window="true"
                :error="errors.license_id ? errors.license_id[0] : null"
                :creatable="false"
                :query="{
                    variant_of: getBaseDocumentDataId('license_id'),
                }"
                :order="{
                    'name': 'asc'
                }"
            />
        </div>

        <div
            class="border bg-white dark:bg-gray-700 rounded-lg w-full p-4 shadow-sm max-w-4xl mb-3"
            v-if="typeId === 5"
        >
            <p class="text-gray-700 dark:text-gray-200 mb-4">* Country data taken from:</p>
            <reference
                create-text="Create new country and link country to this page"
                link-text="Link an existing country"
                v-model="form.country"
                :language="form.language"
                model-data="Country"
                :should-open-new-window="true"
                :error="errors.country_id ? errors.country_id[0] : null"
                :creatable="false"
                :query="{
                    variant_of: getBaseDocumentDataId('country_id'),
                }"
                :order="{
                    'name': 'asc'
                }"
            />
        </div>

        <div
            class="border bg-white dark:bg-gray-700 rounded-lg w-full p-4 shadow-sm max-w-4xl mb-3"
            v-if="typeId === 6"
        >
            <p class="text-gray-700 dark:text-gray-200 mb-4">* Bonus Type data taken from:</p>
            <reference
                create-text="Create new bonus type and link bonus type to this page"
                link-text="Link an existing bonus type"
                v-model="form.bonus_type"
                :language="form.language"
                :should-open-new-window="true"
                model-data="Bonus Type"
                :error="errors.bonus_type_id ? errors.bonus_type_id[0] : null"
                :creatable="false"
                :query="{
                    variant_of: getBaseDocumentDataId('bonus_type_id'),
                }"
                :order="{
                    'name': 'asc'
                }"
            />
        </div>

        <div
            class="border bg-white dark:bg-gray-700 rounded-lg w-full p-4 shadow-sm max-w-4xl mb-3"
            v-if="typeId === 7"
        >
            <p class="text-gray-700 dark:text-gray-200 mb-4">* Game Type data taken from:</p>
            <reference
                create-text="Create new game type and link game type to this page"
                link-text="Link an existing game type"
                v-model="form.game_type"
                :language="form.language"
                :should-open-new-window="true"
                model-data="Game Type"
                :error="errors.game_type_id ? errors.game_type_id[0] : null"
                :creatable="false"
                :query="{
                    variant_of: getBaseDocumentDataId('game_type_id'),
                }"
                :order="{
                    'name': 'asc'
                }"
            />
        </div>

        <div
            class="border bg-white dark:bg-gray-700 rounded-lg w-full p-4 shadow-sm max-w-4xl mb-3"
            v-if="typeId === 8"
        >
            <p class="text-gray-700 dark:text-gray-200 mb-4">* Company Behind data taken from:</p>
            <reference
                link-text="Link an existing company behind"
                v-model="form.company_behind"
                :language="form.language"
                :should-open-new-window="true"
                model-data="Company Behind"
                :error="errors.company_behind_id ? errors.company_behind_id[0] : null"
                :creatable="false"
                :order="{
                    'name': 'asc'
                }"
            />
        </div>

        <base-page-fields
            :form="form"
            :errors="errors"
            :editable-timestamps="true"
            :replace-breadcrumb-enabled="true"
            :publishable="publishable"
        />

        <input-text
            name="ranking_h2"
            label="Ranking H2"
            v-model="form.ranking_h2"
            class="mt-3"
            :error="errors.ranking_h2 ? errors.ranking_h2[0] : null"
        />
        <p class="my-1 text-xs text-gray-600 dark:text-gray-200">You can use these available shortcodes in this H2 ranking field: [current_month], [month], [current_year], [year] and [current_abrev_month]</p>

        <input-text
            name="ranking_h2_anchor"
            label="Jump Id for Ranking H2 (excluding #)"
            v-model="form.ranking_h2_anchor"
            class="mt-3"
            :error="errors.ranking_h2_anchor ? errors.ranking_h2_anchor[0] : null"
        />
        <p class="my-1 text-xs text-gray-600 dark:text-gray-200">You can use these available shortcodes in this H2 ranking anchor field: [current_month], [month], [current_year], [year] and [current_abrev_month]</p>

        <input-text
            name="ranking_h2_anchor_text"
            label="Quick Navi Menu anchor text for Ranking H2"
            v-model="form.ranking_h2_anchor_text"
            class="mt-3"
            :error="errors.ranking_h2_anchor_text ? errors.ranking_h2_anchor_text[0] : null"
        />
        <p class="my-1 text-xs text-gray-600 dark:text-gray-200">You can use these available shortcodes in this H2 ranking anchor text field: [current_month], [month], [current_year], [year] and [current_abrev_month]</p>

        <div class="border bg-white dark:bg-gray-700 rounded-lg w-full p-4 max-w-4xl mt-3">
            <p class="text-gray-700 dark:text-gray-200 mb-4">Ranking or Bonus List (you can only use one)</p>

            <div
                class="border bg-white dark:bg-gray-700 rounded-lg w-full p-4 shadow-sm max-w-4xl"
            >
                <p class="text-gray-700 dark:text-gray-200 mb-4">Ranking</p>
                <reference
                    create-text="Create new ranking and link ranking to this page"
                    link-text="Link an existing ranking"
                    v-model="form.ranking"
                    model-data="Ranking"
                    :disabled="pageHasBonusList"
                    :should-open-new-window="true"
                    :error="errors.ranking_id ? errors.ranking_id[0] : null"
                    :language="form.language"
                    :order="{
                        'name': 'asc'
                    }"
                    :creatable="false"
                />
            </div>

            <div
                class="border bg-white dark:bg-gray-700 rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
            >
                <p class="text-gray-700 dark:text-gray-200 mb-4">Bonus List Top</p>

                <bonus-list
                    :language="form.language"
                    v-model="form.bonus_list_top"
                    :disabled="!! (form.ranking || contentSectionsWithRanking.length)"
                    :document-class="className"
                    :document-data-id="form.software_id || form.payment_id || form.license_id || form.country_id || form.bonus_type_id || form.game_type_id || form.company_behind_id"
                    :document-type-id="typeId"
                />
            </div>
        </div>

        <div
            class="border bg-white dark:bg-gray-700 rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 dark:text-gray-200 mb-4">Author</p>
            <reference
                create-text="Create new author and link author to this page"
                link-text="Link an existing author profile"
                v-model="form.author"
                model-data="Author"
                :should-open-new-window="true"
                :excluded-ids="excludedAuthors"
                :query="{
                    status_id: 1
                }"
                :order="{
                    'authors.name': 'asc'
                }"
                :error="errors.author_id ? errors.author_id[0] : null"
                :creatable="false"
                :language="form.language"
            />
        </div>
        <div
            class="border bg-white dark:bg-gray-700 rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 dark:text-gray-200 mb-4">Fact Checked By</p>
            <reference
                create-text="Create new author and link author to this page"
                link-text="Link an existing author profile"
                v-model="form.fact_checked_by"
                :should-open-new-window="true"
                model-data="Fact Checked By"
                :query="{
                    status_id: 1
                }"
                :order="{
                    'authors.name': 'asc'
                }"
                :excluded-ids="excludedFactCheckers"
                :error="
                    errors.fact_checked_by_id
                        ? errors.fact_checked_by_id[0]
                        : null
                "
                :creatable="false"
                :language="form.language"
            />
        </div>

        <input-rich-text
            name="overview"
            label="Intro Text (below H1)"
            v-model="form.overview"
            class="mt-2"
            :language="form.language"
            :error="errors.overview ? errors.overview[0] : null"
            :maxlength="60000"
        />
        <p class="my-1 text-xs text-gray-600 dark:text-gray-200">You can use these available shortcodes in Intro Text (below H1) field: [current_month], [month], [current_year], [year] and [current_abrev_month]</p>

        <atf-tile
            v-model="form.atf_tile"
            :language="form.language"
            document-type="App\Models\Category"
            :tiles-count="3"
            :errors="errors"
        />

        <atf-accordion-list
            v-model="form.atf_accordion_list"
            :language="form.language"
            :errors="errors"
        />

        <div class="mt-2">
            <span class="text-gray-700 dark:text-gray-200">Content Blocks</span>
            <body-content
                :gallery="form.gallery"
                :language="form.language"
                v-model="form.contentBlocks"
                :errors="errors"
                :additional-casinos-component="additionalCasinosComponent"
                :is-payment-category-page="form.type_id === 3"
                :payment-method-id="form.payment_id"
                :content-section-with-how-to="contentSectionWithHowTo"
                :content-section-with-bonus-list-top="contentSectionWithBonusListTop"
                :content-section-with-bonus-list-bottom="contentSectionWithBonusListBottom"
                :content-sections-with-ranking="contentSectionsWithRanking"
                :document-class="className"
                :document-id="data ? data.id : null"
                :document-data-id="form.software_id || form.payment_id || form.license_id || form.country_id || form.bonus_type_id || form.game_type_id || form.company_behind_id"
                :document-type-id="typeId"
                :has-bonus-lists="! form.ranking"
                :has-ranking="! pageHasBonusList"
                @add-content-block="addContentBlock"
                @add-content-section="addContentSection"
                @delete-content-block="deleteContentBlock"
                @delete-content-section="deleteContentSection"
                @load-content-blocks="loadContentBlocks"
                @add-how-to="howToAdded"
                @delete-how-to="howToDeleted"
                @add-bonus-list-top="bonusListTopAdded"
                @delete-bonus-list-top="bonusListTopDeleted"
                @add-bonus-list-bottom="bonusListBottomAdded"
                @delete-bonus-list-bottom="bonusListBottomDeleted"
                @add-ranking="rankingAdded"
                @delete-ranking="rankingDeleted"
            />
        </div>

        <div
            class="border bg-white dark:bg-gray-700 rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 dark:text-gray-200 mb-4">Sticky CTA</p>
            <reference
                create-text="Create new Sticky CTA and link Sticky CTA to this page"
                link-text="Link existing Sticky CTA"
                v-model="form.stickyCTA"
                :should-open-new-window="true"
                model-data="Sticky-C-T-A"
                :language="form.language"
                :editable="true"
            />
        </div>

        <input type="hidden" name="_token" :value="csrf" />
        <input v-if="data" type="hidden" name="_method" value="patch" />
    </form>
</template>

<script>
import crudForm from "../mixins/crudForm";
import pageFormData from "../mixins/pageFormData";
import contentBlocksForm from "../mixins/contentBlocksForm";
import TableWithBigList from "./TableWithBigList/TableWithBigList.vue";

export default {
    components: { TableWithBigList },
    mixins: [crudForm, contentBlocksForm, pageFormData],
    name: "CategoryForm",
    props: {
        typeId: {
            type: Number,
            required: true,
        },
    },
    data() {
        return {
            form: {
                type_id: this.typeId,
                editor_note: this.getFormData("editor_note"),
                h1_anchor: this.getFormData("h1_anchor"),
                overview: this.getFormData("overview"),
                ranking_id: this.getFormData("ranking_id"),
                ranking: this.getFormData("ranking"),
                ranking_h2: this.getFormData("ranking_h2"),
                ranking_h2_anchor: this.getFormData("ranking_h2_anchor"),
                ranking_h2_anchor_text: this.getFormData("ranking_h2_anchor_text"),
                bonus_list_top: this.getFormData("bonus_list_top", {}),
                author_id: this.getFormData("author_id"),
                author: this.getFormData("author"),
                fact_checked_by_id: this.getFormData("fact_checked_by_id"),
                fact_checked_by: this.getFormData("fact_checked_by"),
                software_id: this.getFormData("software_id"),
                software: this.getFormData("software"),
                payment_id: this.getFormData("payment_id"),
                payment: this.getFormData("payment_method"),
                license_id: this.getFormData("license_id"),
                license: this.getFormData("license"),
                country_id: this.getFormData("country_id"),
                country: this.getFormData("country"),
                bonus_type_id: this.getFormData("bonus_type_id"),
                bonus_type: this.getFormData("bonus_type"),
                game_type_id: this.getFormData("game_type_id"),
                game_type: this.getFormData("game_type"),
                company_behind_id: this.getFormData("company_behind_id"),
                company_behind: this.getFormData("company_behind"),
                stickyCTA: this.getFormData("sticky_c_t_a"),
                sticky_cta_id: this.getFormData("sticky_cta_id"),
                created_at: this.getFormData("created_at") || new Date(),
                created_at_override: this.getFormData("created_at_override"),
                updated_at: this.getFormData("updated_at") || new Date(),
                updated_at_override: this.getFormData("updated_at_override"),
                variants: this.getFormData("variants", []),
                atf_tile: this.getFormData("atf_tile", {use_atf_tiles: 0}),
                atf_accordion_list: this.getFormData("atf_accordion_list", {use_atf_accordion_list: 0}),
            },
        };
    },
    computed: {
        pageHasBonusList() {
            return !! (
                (this.form.bonus_list_top.internal_links_to_categories && Object.keys(this.form.bonus_list_top.internal_links_to_categories).length)
                || (this.form.bonus_list_top.bonusList && this.form.bonus_list_top.bonusList.length)
                || this.contentSectionWithBonusListBottom
            );
        },
        excludedAuthors() {
            return [this.form.fact_checked_by_id];
        },
        excludedFactCheckers() {
            return [this.form.author_id];
        },
        excludedTypes() {
            switch(this.typeId) {
                case 1: return [];
                case 2: return ['Software Category'];
                case 3: return ['Payment Category'];
                case 4: return ['License Category'];
                case 5: return ['Country Category'];
                case 6: return ['Bonus Type Category'];
                case 7: return ['Game Type Category'];
                case 8: return ['Company Behind Category'];
                default: return [];
            }
        },
        additionalCasinosComponent() {
            let additionalCasinosComponent;

            switch(this.excludedTypes[0]) {
                case 'Software Category':
                    additionalCasinosComponent = {
                        name: 'List of Casinos With This Software',
                        shouldShow: true
                    }
                    break;
                case 'Payment Category':
                    additionalCasinosComponent = {
                        name: 'List of Casinos With This Payment Method',
                        shouldShow: true
                    }
                    break;
                case 'License Category':
                    additionalCasinosComponent = {
                        name: 'List of Casinos With This License',
                        shouldShow: true
                    }
                    break;
                case 'Country Category':
                    additionalCasinosComponent = {
                        name: 'List of Casinos Available in this Country',
                        shouldShow: true
                    }
                    break;
                case 'Bonus Type Category':
                    additionalCasinosComponent = {
                        name: 'List of Casinos With This Bonus Type',
                        shouldShow: true
                    }
                    break;
                case 'Game Type Category':
                    additionalCasinosComponent = {
                        name: 'List of Casinos With This Game Type',
                        shouldShow: true
                    }
                    break;
                case 'Company Behind Category':
                    additionalCasinosComponent = {
                        name: 'List of Casinos With This Company Behind',
                        shouldShow: true
                    }
                    break;
            }
            return additionalCasinosComponent;
        },
        overviewCountdown () {
            this.remainingOverviewCount = this.form.overview ? this.maxOverviewCount - this.form.overview.length : this.maxOverviewCount;
        },
    },
    methods: {
        getBaseDocumentDataId(attribute){
            return this.form.baseDocument ? this.form?.baseDocument[attribute] : null;
        }
    },
    watch: {
        "form.author": function (newValue) {
            this.form.author_id = newValue ? newValue.id : null;
        },
        "form.software": function (newValue) {
            this.form.software_id = newValue ? newValue.id : null;
        },
        "form.payment": function (newValue) {
            this.form.payment_id = newValue ? newValue.id : null;
        },
        "form.license": function (newValue) {
            this.form.license_id = newValue ? newValue.id : null;
        },
        "form.country": function (newValue) {
            this.form.country_id = newValue ? newValue.id : null;
        },
        "form.bonus_type": function (newValue) {
            this.form.bonus_type_id = newValue ? newValue.id : null;
        },
        "form.game_type": function (newValue) {
            this.form.game_type_id = newValue ? newValue.id : null;
        },
        "form.company_behind": function (newValue) {
            this.form.company_behind_id = newValue ? newValue.id : null;
        },
        "form.ranking": function (newValue) {
            this.form.ranking_id = newValue ? newValue.id : null;
        },
        "form.fact_checked_by": function (newValue) {
            this.form.fact_checked_by_id = newValue ? newValue.id : null;
        },
        "form.stickyCTA": function (newValue) {
            this.form.sticky_cta_id = newValue ? newValue.id : null;
        }
    }
};
</script>
